import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { SUBJECTS_FALLBACK } from "../lib/subjects";
import { TeacherCard, TeacherCardSkeleton } from "../components/TeacherCard";
import FeaturedCarousel from "../components/FeaturedCarousel";

export default function Search() {
  const [subjects, setSubjects] = useState(SUBJECTS_FALLBACK);
  const [selected, setSelected] = useState(new Set());
  const [openCategory, setOpenCategory] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teachingLocation, setTeachingLocation] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const cityDebounceRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    // Auto-load all approved mentors on first visit (no subject filter)
    fetchTeachers([], "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeachers = async (selectedArr, locFilter = teachingLocation, cityVal = cityFilter) => {
    setLoading(true);
    try {
      const params = {};
      if (selectedArr.length > 0) params.subjects = selectedArr.join(",");
      if (locFilter) params.teaching_location = locFilter;
      if (cityVal?.trim()) params.location = cityVal.trim();
      const { data } = await api.get("/teachers", { params });
      setTeachers(data);
    } finally {
      setLoading(false);
    }
  };

  const onCityChange = (val) => {
    setCityFilter(val);
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      fetchTeachers(Array.from(selected), teachingLocation, val);
    }, 350);
  };

  const toggleCategory = (category) => {
    setOpenCategory((prev) => (prev === category ? null : category));
  };

  const toggleSubject = async (subject) => {
    const next = new Set(selected);
    if (next.has(subject)) next.delete(subject);
    else next.add(subject);
    setSelected(next);
    await fetchTeachers(Array.from(next), teachingLocation, cityFilter);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const onTeachingLocationChange = async (loc) => {
    setTeachingLocation(loc);
    await fetchTeachers(Array.from(selected), loc, cityFilter);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const matchCount = teachers.length;

  return (
    <div className="bg-peach min-h-screen" data-testid="search-page">
      {/* Featured carousel — auto-hides if no featured mentors */}
      <div className="pt-6 sm:pt-10">
        <FeaturedCarousel />
      </div>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-6">
        <h1
          className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight"
          style={{ color: "#13A699" }}
          data-testid="search-heading"
        >
          Find Your Expert
        </h1>
        <p className="mt-2 text-sm sm:text-base" style={{ color: "#13A699" }}>
          Choose a category, then tick the skills you're interested in.
        </p>

        <div className="mt-5 flex items-center gap-3 flex-wrap" data-testid="teaching-location-filter">
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "#13A699" }} htmlFor="teaching-location-select">
            Mentoring Location
          </label>
          <select
            id="teaching-location-select"
            value={teachingLocation}
            onChange={(e) => onTeachingLocationChange(e.target.value)}
            className="rounded-full border border-black/10 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699] font-bold"
            data-testid="teaching-location-select"
          >
            <option value="">Any</option>
            <option value="explorer_home">Explorer home visit</option>
            <option value="mentor_location">Mentor location visit</option>
          </select>
          {teachingLocation && (
            <button
              type="button"
              onClick={() => onTeachingLocationChange("")}
              className="text-xs font-bold underline"
              style={{ color: "#13A699" }}
              data-testid="teaching-location-clear"
            >
              Clear
            </button>
          )}

          <div className="w-full sm:w-auto sm:ml-4 flex items-center gap-2">
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "#13A699" }} htmlFor="city-filter-input">
              City
            </label>
            <input
              id="city-filter-input"
              type="text"
              value={cityFilter}
              onChange={(e) => onCityChange(e.target.value)}
              placeholder="e.g. Bengaluru"
              className="rounded-full border border-black/10 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699] font-bold w-full sm:w-56"
              data-testid="city-filter-input"
            />
            {cityFilter && (
              <button
                type="button"
                onClick={() => onCityChange("")}
                className="text-xs font-bold underline"
                style={{ color: "#13A699" }}
                data-testid="city-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6">
        <div
          className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden"
          data-testid="subjects-list"
        >
          {Object.entries(subjects).map(([category, subs]) => {
            const isOpen = openCategory === category;
            const itemsBySub = Object.entries(subs); // [[subcat, items[]], ...]
            const totalSelectedInCategory = itemsBySub.reduce(
              (acc, [, items]) => acc + items.filter((i) => selected.has(i)).length,
              0
            );
            return (
              <div key={category} className="border-b border-black/5 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-5 sm:px-7 py-5 text-left hover:bg-[#FFF7ED] transition-colors duration-150"
                  data-testid={`category-toggle-${category}`}
                  aria-expanded={isOpen}
                >
                  <span
                    className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight"
                    style={{ color: "#13A699" }}
                  >
                    {category}
                  </span>
                  <span className="flex items-center gap-3">
                    {totalSelectedInCategory > 0 && (
                      <span
                        className="text-xs font-bold px-2 py-1 rounded-full"
                        style={{ background: "#FFD708", color: "#13A699" }}
                        data-testid={`category-count-${category}`}
                      >
                        {totalSelectedInCategory}
                      </span>
                    )}
                    <span
                      className="font-display text-base font-bold uppercase"
                      style={{ color: "#13A699" }}
                    >
                      {isOpen ? "Close" : "Open"}
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div
                    className="px-5 sm:px-7 pb-4 border-t border-black/5"
                    data-testid={`category-content-${category}`}
                  >
                    {itemsBySub.map(([subcat, items]) => (
                      <div key={subcat} className="pt-4">
                        <h3
                          className="font-display text-base sm:text-lg font-bold uppercase tracking-wider mb-1"
                          style={{ color: "#13A699" }}
                        >
                          {subcat}
                        </h3>
                        <ul>
                          {items.map((item) => {
                            const checked = selected.has(item);
                            return (
                              <li
                                key={item}
                                className="flex items-center justify-between py-3 border-b border-black/5 last:border-b-0 cursor-pointer"
                                onClick={() => toggleSubject(item)}
                                data-testid={`subject-row-${item}`}
                              >
                                <span
                                  className="text-base sm:text-lg font-medium"
                                  style={{ color: "#13A699" }}
                                >
                                  {item}
                                </span>
                                <button
                                  type="button"
                                  aria-pressed={checked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSubject(item);
                                  }}
                                  className={`flex items-center justify-center h-7 w-7 sm:h-8 sm:w-8 rounded-md border-2 transition-all duration-200 ${
                                    checked ? "border-[#FFD708]" : "border-[#13A699]/40 bg-white"
                                  }`}
                                  style={checked ? { background: "#FFD708" } : {}}
                                  data-testid={`subject-checkbox-${item}`}
                                >
                                  {checked && (
                                    <span
                                      className="font-display font-extrabold text-base"
                                      style={{ color: "#13A699" }}
                                    >
                                      ✓
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* RESULTS */}
      <section
        ref={resultsRef}
        className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 pb-20"
        data-testid="results-section"
      >
        {selected.size === 0 && !teachingLocation && !cityFilter.trim() ? (
          <div
            className="bg-white rounded-3xl border border-black/5 p-10 text-center"
            data-testid="select-prompt"
          >
            <p
              className="font-display text-xl font-bold uppercase"
              style={{ color: "#13A699" }}
            >
              Tick a Skill to See Experts
            </p>
            <p className="mt-2 text-sm" style={{ color: "#13A699" }}>
              Open any category above and select one or more skills you're looking for.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase"
                style={{ background: "#FFD708", color: "#13A699" }}
                data-testid="match-count-badge"
              >
                Found {matchCount} Expert {matchCount === 1 ? "Match" : "Matches"}
              </span>
              {loading && (
                <span className="text-sm" style={{ color: "#13A699" }}>
                  Loading…
                </span>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <TeacherCardSkeleton key={`sk-${i}`} />)
                : teachers.map((t) => <TeacherCard key={t.id} teacher={t} />)}
            </div>

        {!loading && teachers.length === 0 && (
          <div
            className="mt-8 bg-white rounded-2xl border border-black/5 p-10 text-center"
            data-testid="empty-state"
          >
            <p className="font-display text-lg" style={{ color: "#13A699" }}>
              No mentors found yet. Try ticking different subjects, or come back soon!
            </p>
          </div>
        )}
          </>
        )}
      </section>
    </div>
  );
}
